import {
  RestaurantSetting,
  RestaurantSettingsResponse,
  BulkRestaurantSettingsResponse,
} from "@lemmonhq/types";
import { getAPIClient } from "../helpers";
import { useAuth } from "@app/vue/providers/Auth";

export const settingsAPI = {
  /**
   * Get all restaurant settings with optional filters
   */
  getSettings: async (params?: {
    key?: string;
    model_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<RestaurantSettingsResponse> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.settings.$get({
      query: params as any,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as RestaurantSettingsResponse;
    }

    throw new Error("Failed to fetch restaurant settings");
  },

  /**
   * Create a new restaurant setting
   */
  createSetting: async (data: {
    key: string;
    value: string;
    value_type?: string;
    model_type?: string;
  }): Promise<{ data: RestaurantSetting }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.settings.$post({
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as { data: RestaurantSetting };
    }

    throw new Error("Failed to create restaurant setting");
  },

  /**
   * Update a restaurant setting by key
   */
  updateSetting: async (
    key: string,
    data: {
      value: string;
      value_type?: string;
    },
  ): Promise<{ data: RestaurantSetting }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.settings[":key"].$put({
      param: { key },
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as { data: RestaurantSetting };
    }

    throw new Error("Failed to update restaurant setting");
  },

  /**
   * Delete a restaurant setting by key
   */
  deleteSetting: async (key: string): Promise<{ success: boolean }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.settings[":key"].$delete({
      param: { key },
    });

    if (resp.ok) {
      const json = await resp.json();
      return json as { success: boolean };
    }

    throw new Error("Failed to delete restaurant setting");
  },

  /**
   * Bulk upsert restaurant settings
   */
  bulkUpdateSettings: async (
    data: {
      settings: Array<{
        key: string;
        value: string;
        value_type?: string;
      }>;
      model_type?: string;
    },
    auth: ReturnType<typeof useAuth>,
  ): Promise<BulkRestaurantSettingsResponse> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.settings.bulk.$post({
      json: data,
    });

    if (resp.ok) {
      const json = await resp.json();
      await auth.refetchSession();
      return json as BulkRestaurantSettingsResponse;
    }

    throw new Error("Failed to bulk update restaurant settings");
  },
};
